import React, { useState, useEffect } from 'react';
import { Syringe, Stethoscope, CalendarCheck, CalendarX, Wallet, DoorOpen, Cake, ChevronDown, Save, Info, Send } from 'lucide-react';
import { getSmsSettings, updateSmsSettings, sendManualSms } from '../../services/api';
import { toast } from 'sonner';

const SMS_TYPES = [
  { key: 'vaccine_reminder', label: 'Aşı Hatırlatma', desc: 'Aşı tarihi yaklaştığında müşteriye SMS gönderir', icon: Syringe, color: 'text-[#34C759]', bg: 'bg-green-50', hasDays: true },
  { key: 'treatment_reminder', label: 'Tedavi Hatırlatma', desc: 'Tedavi tekrar tarihi yaklaştığında müşteriye SMS gönderir', icon: Stethoscope, color: 'text-[#6b6de3]', bg: 'bg-blue-50', hasDays: true },
  { key: 'appointment_confirmed', label: 'Randevu Onayı', desc: 'Randevu onaylandığında müşteriye bildirim gönderir', icon: CalendarCheck, color: 'text-[#007AFF]', bg: 'bg-sky-50', hasDays: false },
  { key: 'appointment_rejected', label: 'Randevu Reddi', desc: 'Randevu reddedildiğinde müşteriye bildirim gönderir', icon: CalendarX, color: 'text-[#FF3B30]', bg: 'bg-red-50', hasDays: false },
  { key: 'balance_reminder', label: 'Bakiye Hatırlatma', desc: 'Borcu olan müşterilere hatırlatma gönderir', icon: Wallet, color: 'text-[#FF9500]', bg: 'bg-orange-50', hasDays: false },
  { key: 'boarding_checkout', label: 'Konaklama Bildirimi', desc: 'Konaklama tamamlandığında müşteriye bildirim gönderir', icon: DoorOpen, color: 'text-[#AF52DE]', bg: 'bg-purple-50', hasDays: false },
  { key: 'birthday_greeting', label: 'Doğum Günü Kutlama', desc: 'Pet doğum gününde müşteriye kutlama mesajı gönderir', icon: Cake, color: 'text-[#FF2D55]', bg: 'bg-pink-50', hasDays: true },
];

const DAY_OPTIONS = [
  { value: 0, label: 'Aynı gün' },
  { value: 1, label: '1 gün önce' },
  { value: 2, label: '2 gün önce' },
  { value: 3, label: '3 gün önce' },
  { value: 5, label: '5 gün önce' },
  { value: 7, label: '7 gün önce' },
];

const SmsSettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testingKey, setTestingKey] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSmsSettings();
      setSettings(res.data);
    } catch {
      toast.error('SMS ayarları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key]?.enabled }
    }));
  };

  const handleDayToggle = (key, day) => {
    setSettings(prev => {
      const current = prev[key]?.days_before || [];
      const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day].sort((a, b) => a - b);
      return { ...prev, [key]: { ...prev[key], days_before: updated } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSmsSettings(settings);
      toast.success('SMS ayarları kaydedildi');
    } catch {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async (typeKey) => {
    const phone = testPhone.trim();
    if (!phone) {
      toast.error('Lütfen test telefon numarası girin');
      return;
    }
    const cfg = settings?.[typeKey] || {};
    const template = cfg.template || '';
    if (!template) {
      toast.error('Bu tür için şablon bulunamadı');
      return;
    }
    // Replace placeholders with sample data
    const message = template
      .replace(/{customer_name}/g, 'Test Müşteri')
      .replace(/{pet_name}/g, 'Test Pet')
      .replace(/{operation_name}/g, 'Karma Aşı')
      .replace(/{due_date}/g, new Date().toLocaleDateString('tr-TR'))
      .replace(/{date}/g, new Date().toLocaleDateString('tr-TR'))
      .replace(/{amount}/g, '500,00 TL');

    setTestingKey(typeKey);
    try {
      const res = await sendManualSms({
        phone,
        message,
        customer_name: 'Test Müşteri',
        sms_type: `test_${typeKey}`,
      });
      if (res.data?.success) {
        toast.success('Test SMS gönderildi');
      } else {
        toast.error('Test SMS gönderilemedi');
      }
    } catch {
      toast.error('Test SMS gönderilemedi');
    } finally {
      setTestingKey(null);
    }
  };

  if (loading) return <div className="text-center py-8 text-[#8E8E93] text-sm">Yükleniyor...</div>;

  return (
    <div className="space-y-3">
      {/* Info Banner */}
      <div className="bg-blue-50 rounded-2xl p-3 flex items-start gap-2.5">
        <Info size={16} className="text-[#007AFF] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-medium text-[#007AFF]">SMS Otomasyon Ayarları</p>
          <p className="text-[10px] text-[#007AFF]/70 mt-0.5">Burada açtığınız bildirimler otomatik olarak tetiklenir. Müşteri bazında kapatma seçeneği müşteri sayfasından yapılabilir. Mesaj şablonları sistem yöneticisi tarafından belirlenir.</p>
        </div>
      </div>

      {/* Test Phone Input */}
      <div className="bg-white rounded-2xl px-4 py-3" data-testid="sms-test-phone-section">
        <p className="text-xs font-semibold text-gray-900 mb-2">Test SMS Gönderimi</p>
        <div className="flex gap-2">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="05XX XXX XX XX"
            className="flex-1 h-9 px-3 bg-[#F2F2F7] border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#6b6de3]/30"
            data-testid="sms-test-phone-input"
          />
        </div>
        <p className="text-[10px] text-[#8E8E93] mt-1.5">Numara girin, ardından her SMS türünün yanındaki "Test Et" butonuyla o şablonu test edin.</p>
      </div>

      {/* SMS Type Cards */}
      {SMS_TYPES.map(type => {
        const Icon = type.icon;
        const cfg = settings?.[type.key] || {};
        const isEnabled = cfg.enabled || false;
        const isExpanded = expandedKey === type.key;
        const days = cfg.days_before || [];

        return (
          <div key={type.key} className="bg-white rounded-2xl overflow-hidden" data-testid={`sms-${type.key}`}>
            {/* Header Row */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className={`w-9 h-9 rounded-xl ${type.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={18} className={type.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{type.label}</p>
                <p className="text-[10px] text-[#8E8E93] leading-tight">{type.desc}</p>
              </div>
              {/* Toggle + Test */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {testPhone.trim() && (
                  <button
                    onClick={() => handleTestSms(type.key)}
                    disabled={testingKey === type.key}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#6b6de3]/10 hover:bg-[#6b6de3]/20 text-[#6b6de3] text-[10px] font-semibold transition-colors disabled:opacity-50"
                    data-testid={`test-sms-${type.key}`}
                  >
                    <Send size={10} />
                    {testingKey === type.key ? '...' : 'Test Et'}
                  </button>
                )}
                <button
                  onClick={() => handleToggle(type.key)}
                  className={`w-12 h-7 rounded-full transition-colors relative ${isEnabled ? 'bg-[#34C759]' : 'bg-gray-200'}`}
                  data-testid={`toggle-${type.key}`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-5.5 left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            </div>

            {/* Day selection (only for types with hasDays, when enabled) */}
            {isEnabled && type.hasDays && (
              <div className="border-t border-gray-50">
                <button
                  onClick={() => setExpandedKey(isExpanded ? null : type.key)}
                  className="w-full flex items-center justify-between px-4 py-2 text-[10px] text-[#8E8E93] hover:bg-gray-50"
                >
                  <span>Gönderim zamanı: {days.map(d => d === 0 ? 'Aynı gün' : `${d} gün önce`).join(', ') || 'Seçilmedi'}</span>
                  <ChevronDown size={12} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3">
                    <div className="flex flex-wrap gap-1.5">
                      {DAY_OPTIONS.map(opt => {
                        const isActive = days.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            onClick={() => handleDayToggle(type.key, opt.value)}
                            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${isActive ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-600 hover:bg-gray-200'}`}
                            data-testid={`day-${type.key}-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-[#6b6de3] text-white py-3 rounded-2xl font-semibold text-sm hover:bg-[#5b5dd3] transition-colors disabled:opacity-50"
        data-testid="save-sms-settings"
      >
        <Save size={16} />
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </button>
    </div>
  );
};

export default SmsSettingsTab;
